import { BalanceData, ChestBand, Rarity } from "../data/balance";
import { Fix64 } from "../maths/fix64";
import { Pcg32 } from "../maths/pcg32";

export interface ChestResult {
  readonly band: ChestBand;
  readonly rarity: Rarity;
  /** Level cleared = chest level (gear level requirements come from it). */
  readonly level: number;
  /** Seed the gear inside is generated from, so opening is reproducible. */
  readonly seed: bigint;
  /** Gold (the one resource, DESIGN §9.3): the chest's rolled total. */
  readonly gold: number;
  /** Gear items rolled (§9.3): 1 for Common–Rare, 1–2 for Epic+, one more on a first clear (before the late first
   *  clears) and on some farm runs; generated by the gear model. */
  readonly gearCount: number;
  /** Minutes of speed-up granted (§9.3). */
  readonly speedUpMinutes: number;
}

/** Performance band (§3.3): loss, win, win without leaks, first clear. */
export function chestBand(won: boolean, leaks: number, firstClear: boolean): ChestBand {
  if (!won) return ChestBand.Loss;
  if (firstClear) return ChestBand.FirstClear;
  return leaks === 0 ? ChestBand.WinNoLeak : ChestBand.Win;
}

/**
 * The band a battle's chest rolls from (the free wall, docs/SHOP.md §3.3): `chestBand`, then a win of a level already
 * cleared (a replay, Heroic or Trial run) is a farm run (Farm / FarmNoLeak: better odds, more resources, sometimes one
 * more item: K3), and from `lateFirstClearFrom` a first clear pays `lateFirstClearBand` (a plain Win chest, K2).
 */
export function battleChestBand(balance: BalanceData, level: number, won: boolean, leaks: number, firstClear: boolean): ChestBand {
  const band = chestBand(won, leaks, firstClear);
  const ch = balance.chests;
  if (band === ChestBand.Win) return ChestBand.Farm;
  if (band === ChestBand.WinNoLeak) return ChestBand.FarmNoLeak;
  if (band === ChestBand.FirstClear && ch.lateFirstClearFrom > 0 && level >= ch.lateFirstClearFrom) return ch.lateFirstClearBand;
  return band;
}

/**
 * MP-25 ("generous first clears"): the rarity steps a battle chest is raised — chests.json lateFirstClear.rarityBump on
 * the first clear of a level from `lateFirstClearFrom` (one rarity above the plain Win chest it rolls there), else 0.
 */
export function battleChestBump(balance: BalanceData, level: number, won: boolean, firstClear: boolean): number {
  const ch = balance.chests;
  return won && firstClear && ch.lateFirstClearFrom > 0 && level >= ch.lateFirstClearFrom ? ch.lateFirstClearRarityBump : 0;
}

/** What a roll adds on top of its band (MP-25): rarity steps after the roll, and a share more gold (a farm twist's bonus). */
export interface ChestExtras { readonly rarityBump: number; readonly goldBonus: Fix64 }
export const NO_CHEST_EXTRAS: ChestExtras = { rarityBump: 0, goldBonus: Fix64.Zero };

/** A farm run's chest (K3): its resources are scaled and it may hold one more item. */
export const isFarmBand = (band: ChestBand): boolean => band === ChestBand.Farm || band === ChestBand.FarmNoLeak;
const PERCENT = 100;

/**
 * A band's rarity weights at chest level `level` (DESIGN §9.2, M30 #12 — Kevin: "Epic gear way too early; drops should
 * climb gradually from Common, else lesser rarities are useless"): each rarity keeps
 * floor(weight × (level − climbFrom + 1) / (climbFull − climbFrom + 1)) of its weight from climbFrom (none before it,
 * all from climbFull), and what it has not earned falls to the rarity below, top down, so the total — and
 * with it the roll's random draw — is unchanged. Integer maths: the same on every platform.
 */
export function bandWeightsAt(balance: BalanceData, band: ChestBand, level: number): number[] {
  const ch = balance.chests;
  const w = [...ch.bandWeights[band]];
  for (let r = w.length - 1; r > 0; r--) {
    const from = ch.climbFrom[r], full = ch.climbFull[r];
    if (level >= full || w[r] === 0) continue;
    const kept = level < from ? 0 : Math.floor((w[r] * (level - from + 1)) / (full - from + 1));
    w[r - 1] += w[r] - kept;
    w[r] = kept;
  }
  return w;
}

/**
 * Rolls rarity from the band table (§9.2) at the level's rarity climb (`bandWeightsAt`; `climb` false rolls the band
 * as it stands, for a chest of a fixed rarity) and the gold (§9.3) for a level. Deterministic from the seed.
 * A first clear of the Starter level pays the fixed Starter chest instead; early first clears have a rarity floor
 * (`earlyGear`), and early levels' resources are boosted (`earlyResourceMult`).
 */
export function rollChest(balance: BalanceData, level: number, band: ChestBand, seed: bigint, climb = true, extras: ChestExtras = NO_CHEST_EXTRAS): ChestResult {
  const ch = balance.chests;
  const starter = ch.starter;
  if (starter !== null && band === ChestBand.FirstClear && level === starter.level) {
    return { band, rarity: starter.rarity, level, seed, gold: starter.gold, gearCount: starter.gearCount, speedUpMinutes: ch.speedUpMinutes[starter.rarity] };
  }
  const weights = climb ? bandWeightsAt(balance, band, level) : ch.bandWeights[band];
  let total = 0;
  for (const w of weights) total += w;
  const rng = new Pcg32(seed, 91);
  let r = rng.nextInt(total);
  let rarity = Rarity.Common;
  for (let i = 0; i < weights.length; i++) {
    r -= weights[i];
    if (r < 0) { rarity = i as Rarity; break; }
  }
  if (band === ChestBand.FirstClear) rarity = Math.max(rarity, earlyRarityFloor(balance, level)) as Rarity;
  if (extras.rarityBump > 0) rarity = Math.min(rarity + extras.rarityBump, topClimbedRarity(balance, level)) as Rarity;
  const farm = isFarmBand(band);
  const plain = Fix64.fromInt(ch.resourceBase + ch.resourcePerLevel * level).mul(ch.rarityMult[rarity]).mul(earlyResourceMult(balance, level))
    .mul(farm ? ch.farmResourceMult : Fix64.One);
  const bundle = extras.goldBonus.isZero() ? plain : plain.mul(Fix64.One.add(extras.goldBonus));
  const [gMin, gMax] = ch.gearCount[rarity];
  let extra = band === ChestBand.FirstClear ? ch.firstClearExtraGear : 0;
  // Drawn after the count so a plain chest's rolls are the ones it always had.
  const counted = gMin + (gMax > gMin ? rng.nextInt(gMax - gMin + 1) : 0);
  if (farm && ch.farmExtraGearPercent > 0 && rng.nextInt(PERCENT) < ch.farmExtraGearPercent) extra += 1;
  const gearCount = counted + extra;
  return {
    band, rarity, level, seed,
    gold: bundle.floorToInt(),
    gearCount,
    speedUpMinutes: ch.speedUpMinutes[rarity],
  };
}

/**
 * The highest rarity the level's rarity climb has opened (the First-clear band's top non-zero weight there): a bumped
 * chest never passes it, so chapter 1 stays under Epic (M32) and drops still climb gradually (M30 #12).
 */
export function topClimbedRarity(balance: BalanceData, level: number): Rarity {
  const w = bandWeightsAt(balance, ChestBand.FirstClear, level);
  for (let r = w.length - 1; r > 0; r--) if (w[r] > 0) return r as Rarity;
  return Rarity.Common;
}

/**
 * Chest resource multiplier for a level (§9.3): `earlyResourceMult` through `earlyFullThroughLevel`, then linear down
 * to × 1 at `earlyTaperToLevel` and after. Levels 1–10 are limited by resources, not by timers, so they pay more.
 */
export function earlyResourceMult(balance: BalanceData, level: number): Fix64 {
  const ch = balance.chests;
  const full = ch.earlyFullThroughLevel, end = ch.earlyTaperToLevel;
  if (level <= full) return ch.earlyResourceMult;
  if (level >= end) return Fix64.One;
  return Fix64.One.add(ch.earlyResourceMult.sub(Fix64.One).mulInt(end - level).divInt(end - full));
}

/** Lowest rarity a first-clear chest of this level may roll (§9.3 early gear): Common unless the early floors apply. */
export function earlyRarityFloor(balance: BalanceData, level: number): Rarity {
  const eg = balance.chests.earlyGear;
  if (eg === null || level > eg.throughLevel) return Rarity.Common;
  return level === eg.rareAtLevel ? Math.max(eg.minRarity, Rarity.Rare) as Rarity : eg.minRarity;
}

/**
 * The level whose readiness seats this chest's gear rolls for (§9.3 early gear, §3.4): `earlyGear.forLevel` for a
 * first clear of levels up to `earlyGear.throughLevel` (the Starter chest included), 0 for a plain uniform roll.
 */
export function chestGearTargetLevel(balance: BalanceData, chest: ChestResult): number {
  const eg = balance.chests.earlyGear;
  return eg !== null && chest.band === ChestBand.FirstClear && chest.level <= eg.throughLevel ? eg.forLevel : 0;
}

/** A chest of one fixed rarity through the existing roll: the Win band's weights become that rarity only. */
export function chestOfRarity(balance: BalanceData, level: number, rarity: Rarity, seed: bigint): ChestResult {
  const only = balance.chests.bandWeights[ChestBand.Win].map((_, r) => (r === rarity ? 1 : 0));
  const chests = { ...balance.chests, bandWeights: balance.chests.bandWeights.map((w, b) => (b === ChestBand.Win ? only : w)) };
  return rollChest(Object.assign(Object.create(balance) as BalanceData, { chests }), level, ChestBand.Win, seed, false);
}
